<script setup>
/**
 * Rich ML model selector — model name + R² + ±20% IAAO + creation date.
 *
 * Extracted from ComparisonTable's inline selector so the admin model-explorer
 * and the Kohandustabel share one selector with identical naming/metrics.
 * Filters by model `type` (default 'ebm', so CatBoost similarity models never
 * appear in an EBM picker) and sorts newest-first. v-model holds the model id.
 */
import { computed } from 'vue'
import { useFormatDate } from '@/composables/useFormatDate'

const props = defineProps({
  // Raw model objects from /models: { model, name, type, created_at, metrics }
  models: { type: Array, default: () => [] },
  type: { type: String, default: 'ebm' },
  placeholder: { type: String, default: '' },
  size: { type: String, default: undefined },
})

const emit = defineEmits(['change'])
const selected = defineModel({ type: String, default: null })

const { formatDate } = useFormatDate()

const options = computed(() =>
  (props.models || [])
    .filter(m => m?.type === props.type)
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map(model => ({
      label: model.name,
      value: model.model,
      metrics: model.metrics,
      created_at: model.created_at,
      // Lifecycle marker from the model card (autoresearch bundles): candidates
      // are selectable everywhere but badged so they read as non-default.
      stage: model.stage ?? null,
    })),
)

const isCandidate = value => options.value.find(m => m.value === value)?.stage === 'candidate'

const getModelLabel = value => options.value.find(m => m.value === value)?.label ?? value
const getModelR2 = value => options.value.find(m => m.value === value)?.metrics?.r2_score?.test
// Slice ISO timestamp to yyyy-MM-dd — useFormatDate.formatDate parses date-only
// strings, while the /models endpoint returns full timestamps.
const getModelCreatedAt = value => {
  const created = options.value.find(m => m.value === value)?.created_at
  if (!created) {
    return null
  }
  return formatDate(String(created).slice(0, 10))
}
const formatPercent = value => (value != null ? `${(value * 100).toFixed(1)}%` : '-')

defineExpose({ options })
</script>

<template>
  <Select
    v-model="selected"
    :options="options"
    option-label="label"
    option-value="value"
    :size="size"
    :placeholder="placeholder"
    class="min-w-64"
    @change="emit('change', $event)"
  >
    <template #value="slotProps">
      <div v-if="slotProps.value" class="flex items-center gap-2">
        <span>{{ getModelLabel(slotProps.value) }}</span>
        <Tag v-if="isCandidate(slotProps.value)" value="kandidaat" severity="warn" class="text-[10px]! py-0!" />
        <span v-if="getModelR2(slotProps.value)" class="text-xs text-gray-500">
          R² {{ formatPercent(getModelR2(slotProps.value)) }}
        </span>
        <span v-if="getModelCreatedAt(slotProps.value)" class="text-xs text-gray-500">
          {{ getModelCreatedAt(slotProps.value) }}
        </span>
      </div>
      <span v-else>{{ slotProps.placeholder }}</span>
    </template>
    <template #option="slotProps">
      <div class="flex flex-col gap-0.5 py-1">
        <div class="flex items-center gap-2 font-medium">
          {{ slotProps.option.label }}
          <Tag v-if="slotProps.option.stage === 'candidate'" value="kandidaat" severity="warn" class="text-[10px]! py-0!" />
        </div>
        <div v-if="slotProps.option.metrics || slotProps.option.created_at" class="flex gap-3 text-xs text-gray-500">
          <span v-if="slotProps.option.metrics?.r2_score?.test" class="flex items-center gap-1">
            <span class="font-semibold text-blue-600">R²</span>
            {{ formatPercent(slotProps.option.metrics.r2_score.test) }}
          </span>
          <span v-if="slotProps.option.metrics?.['within_plus_minus20%_iaao']?.test" class="flex items-center gap-1">
            <span class="font-semibold text-green-600">±20%</span>
            {{ formatPercent(slotProps.option.metrics['within_plus_minus20%_iaao'].test / 100) }}
          </span>
          <span v-if="slotProps.option.created_at" class="flex items-center gap-1">
            <span aria-hidden="true">·</span>
            {{ formatDate(String(slotProps.option.created_at).slice(0, 10)) }}
          </span>
        </div>
      </div>
    </template>
  </Select>
</template>
